import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Plane, ArrowRight } from "lucide-react";

/**
 * EUSaudiBanner — surfaces the "KSA actively recruits Europeans" pitch
 * on the candidate dashboard. One-click opt-in to becoming visible to
 * Saudi employers (sets profile.looking_for_work = true).
 *
 * Shown only when:
 *   • Candidate's profile.nationality matches an EU/European country
 *   • Candidate has NOT already set looking_for_work = true
 *   • Candidate hasn't dismissed the banner (localStorage flag)
 *
 * Self-fetches the profile so it can be dropped into any candidate page
 * without prop wiring.
 */

const EU_NATIONALITIES = new Set([
  "france", "french",
  "germany", "german",
  "italy", "italian",
  "united kingdom", "uk", "british", "england", "english", "scottish", "welsh",
  "romania", "romanian",
  "spain", "spanish",
  "portugal", "portuguese",
  "belgium", "belgian",
  "netherlands", "dutch", "holland",
  "poland", "polish",
  "ireland", "irish",
  "greece", "greek",
  "austria", "austrian",
  "sweden", "swedish",
  "denmark", "danish",
  "finland", "finnish",
  "czech republic", "czech",
  "hungary", "hungarian",
  "bulgaria", "bulgarian",
  "croatia", "croatian",
  "slovenia", "slovenian",
  "slovakia", "slovak",
]);

const DISMISS_KEY = "maqam_eu_saudi_banner_dismissed_v1";

export default function EUSaudiBanner() {
  const [profile, setProfile] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1"
  );
  const [optingIn, setOptingIn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/profile")
      .then((r) => { if (!cancelled) setProfile(r.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !profile) return null;

  const nationality = (profile.nationality || "").trim().toLowerCase();
  const isEU = EU_NATIONALITIES.has(nationality);
  if (!isEU) return null;
  if (profile.looking_for_work === true) return null;

  const onOptIn = async () => {
    setOptingIn(true);
    try {
      await api.put("/profile", { looking_for_work: true });
      toast.success("You're now visible to Saudi employers. Welcome.", { duration: 4500 });
      localStorage.setItem(DISMISS_KEY, "1");
      setDismissed(true);
    } catch {
      toast.error("Could not update your profile. Please try again.");
    } finally {
      setOptingIn(false);
    }
  };

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="relative bg-gradient-to-r from-[#0c0c0d] via-[#141416] to-[#0c0c0d] border border-[#FF9500]/40 p-6 md:p-8 overflow-hidden text-white"
      data-testid="eu-saudi-banner"
    >
      {/* Subtle KSA-green strip on the left edge — Saudi flag accent */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#34C759]" />
      {/* Maqam orange glow */}
      <div
        className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,149,0,0.22), transparent 70%)" }}
      />

      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-3 right-3 text-white/40 hover:text-white p-1"
        aria-label="Dismiss"
        data-testid="eu-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div className="ps-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF9500] mb-3 flex items-center gap-2">
            <Sparkles className="w-3 h-3" />
            EUROPEAN CANDIDATE · KSA RECRUITS YOU
          </div>
          <h3 className="font-display font-black text-2xl md:text-3xl tracking-tighter leading-tight mb-3">
            Saudi Arabia is actively recruiting{" "}
            <span className="italic text-[#FF9500]">
              {profile.nationality || "European"}
            </span>{" "}
            talent.
          </h3>
          <p className="text-white/70 text-sm leading-relaxed max-w-2xl">
            Engineers, doctors, architects, finance pros — the Kingdom's Vision 2030
            mega-projects (NEOM, Red Sea, Qiddiya) actively sponsor relocation for
            European specialists.{" "}
            <span className="text-white font-semibold">
              Full visa sponsorship · housing allowance · tax-free salary ·
              annual home flights.
            </span>{" "}
            Most Maqam Europeans don't realize they qualify until employers reach out.
          </p>
        </div>

        <div className="flex flex-col gap-2 md:items-end">
          <button
            type="button"
            onClick={onOptIn}
            disabled={optingIn}
            className="inline-flex items-center justify-center gap-2 bg-[#FF9500] text-black hover:bg-[#FF9500]/85 font-bold px-6 py-4 transition-colors whitespace-nowrap disabled:opacity-60"
            data-testid="eu-banner-optin"
          >
            <Plane className="w-4 h-4" />
            {optingIn ? "Updating…" : "Make me visible to KSA employers"}
            <ArrowRight className="w-4 h-4" />
          </button>
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 md:text-end">
            One click · reversible anytime in Profile
          </span>
        </div>
      </div>
    </div>
  );
}
